import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import SignaturePad from "react-signature-canvas";
import {
  Button,
  Flex,
  Box,
  Text,
  Image,
  Grid,
  GridItem,
  Checkbox,
  Icon,
  IconButton,
  Skeleton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  VStack,
  Circle,
} from "@chakra-ui/react";
import { FaArrowLeft, FaArrowRight, FaFileAlt, FaCheck } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { Storage } from "aws-amplify";
import { apiAuth, apiGetAuth } from "../../../Backend/api";
import { nextIncompleteStep } from "../stepFlow";

// Use a verified worker source from the CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;

// Extract the list of document S3 keys from any of the shapes the API might
// return: flat (`item.documents`), nested (`item.securityItem.documents`), or
// a freshly-fetched SecurityItem response.
const getDocumentKeys = (...sources) => {
  for (const source of sources) {
    if (!source) continue;
    if (Array.isArray(source.documents)) return source.documents;
    if (Array.isArray(source.securityItem?.documents)) {
      return source.securityItem.documents;
    }
  }
  return [];
};

// Unwrap a security-item REST payload regardless of envelope shape.
const unwrapSecurityItem = (response) => {
  if (!response) return null;
  return (
    response.data?.securityItem ||
    response.securityItem ||
    response.data ||
    response
  );
};

export const DocumentSign = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const toast = useToast();
  const { guest, room, booking, storedStripeId, manager, accessTimes } =
    location.state || {};

  // Once this step is done, the guest presses Continue to move on. The
  // state must carry EVERYTHING the step screens and /onboarding read
  // (manager drives extended ID verification; accessTimes feeds the done
  // screen) or a hop through this screen silently degrades the next one.
  const stepNavState = { guest, room, booking, storedStripeId, manager, accessTimes };

  // Back always means the checklist — never history. Continue buttons push
  // step screens onto the history stack, so navigate(-1) would land on the
  // previous (often already-completed) step instead of home. `replace`
  // keeps finished step screens out of the stack entirely, so even the
  // browser's own back button returns to the checklist, not a done step.
  const goToChecklist = () => {
    navigate("/onboarding", {
      state: { ...stepNavState, needsRefresh: true },
      replace: true,
    });
  };

  const goToNextStep = () => {
    const next = nextIncompleteStep(booking, "DOCUMENT");
    if (next) {
      navigate(next.path, { state: stepNavState, replace: true });
    } else {
      goToChecklist();
    }
  };
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [thumbnails, setThumbnails] = useState({});
  const [documents, setDocuments] = useState([]);
  const [signedDocuments, setSignedDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasDocumentItem, setHasDocumentItem] = useState(true);
  const [stepComplete, setStepComplete] = useState(false);
  const [bypassed, setBypassed] = useState(false);
  const signaturePadRef = useRef(null);
  // react-signature-canvas locks the canvas dimensions in at mount and
  // does NOT auto-resize when its container grows. If `window.innerWidth`
  // is briefly 0 / very small (e.g. during a layout pass triggered by the
  // PDF rendering, an iOS keyboard close, or an orientation change), the
  // canvas ends up zero-width and effectively invisible. We work around
  // this by measuring the wrapper element ourselves and remounting the
  // SignaturePad whenever the measured width changes.
  const sigContainerRef = useRef(null);
  const [sigWidth, setSigWidth] = useState(0);
  const SIG_HEIGHT = 150;
  // Same measured-width approach for the PDF page. react-pdf's <Page>
  // takes a fixed pixel width; rendering at `window.innerWidth` overflows
  // (and gets clipped by) the bordered container on desktop where the
  // page is narrower than the viewport.
  const pdfContainerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Resolve signed document URLs for the booking's DOCUMENT security item.
  // 1. Find the BookingSecurityItem of type DOCUMENT on the booking
  // 2. Short-circuit if the step is already complete or was bypassed by the host
  // 3. Try to read document keys directly off it (flat or nested)
  // 4. If keys aren't there, fall back to GET /security/security-items/:id
  // 5. Mint S3 signed URLs for each key
  const fetchBookingDocuments = async () => {
    setLoading(true);
    try {
      const bookingSecurityItems = booking?.securityItems?.items || [];
      const documentSecurityItem = bookingSecurityItems.find(
        (item) => item.type === "DOCUMENT"
      );

      if (!documentSecurityItem) {
        setHasDocumentItem(false);
        setStepComplete(false);
        setBypassed(false);
        setDocuments([]);
        setSignedDocuments([]);
        return;
      }

      setHasDocumentItem(true);

      const isBypassed =
        typeof documentSecurityItem.status === "string" &&
        documentSecurityItem.status.toLowerCase() === "bypass";

      // Host explicitly waived this step — the SecurityItem may not even
      // exist anymore. Don't try to fetch documents in this case.
      if (isBypassed) {
        setBypassed(true);
        setStepComplete(true);
        setDocuments([]);
        setSignedDocuments([]);
        return;
      }

      // Otherwise keep loading documents so previously-signed ones still
      // render (with their green checkmark) for the guest to review.
      setBypassed(false);
      setStepComplete(documentSecurityItem.isComplete === true);

      let documentKeys = getDocumentKeys(documentSecurityItem);

      // Fall back to the SecurityItem endpoint if the booking response didn't
      // include the documents inline.
      if (documentKeys.length === 0 && documentSecurityItem.securityItemID) {
        try {
          const response = await apiGetAuth(
            `/security/security-items/${documentSecurityItem.securityItemID}`
          );
          const fullSecurityItem = unwrapSecurityItem(response);

          if (fullSecurityItem) {
            // Cache it on the in-memory booking object so re-renders / sign
            // flow don't have to refetch.
            documentSecurityItem.securityItem = fullSecurityItem;
            documentKeys = getDocumentKeys(
              documentSecurityItem,
              fullSecurityItem
            );
          }
        } catch (lookupError) {
          console.error("Error fetching SecurityItem:", lookupError);
        }
      }

      if (documentKeys.length === 0) {
        setDocuments([]);
        setSignedDocuments([]);
        return;
      }

      const documentURLs = await Promise.all(
        documentKeys.map(async (key) => {
          try {
            const signedURL = await Storage.get(key);
            return { id: key, name: key.split("/").pop(), file: signedURL };
          } catch (error) {
            console.error(`Error fetching document ${key}:`, error);
            return null;
          }
        })
      );

      const validDocuments = documentURLs.filter(Boolean);
      setDocuments(validDocuments);

      const booleanList = documentSecurityItem.isCompleteSignatures || [];
      setSignedDocuments(
        validDocuments.map((_, index) => booleanList[index] || false)
      );
    } catch (error) {
      console.error("Error preparing documents for signing:", error);
      toast({
        title: "Error",
        description: "Failed to load documents for signing.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking]);

  useEffect(() => {
    const generateThumbnails = async () => {
      if (documents.length === 0) return;

      const generatedThumbnails = {};

      for (const doc of documents) {
        try {
          const pdf = await pdfjs.getDocument(doc.file).promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 0.3 });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const context = canvas.getContext("2d");

          await page.render({ canvasContext: context, viewport }).promise;
          generatedThumbnails[doc.id] = canvas.toDataURL();
        } catch (error) {
          console.error("Error generating thumbnail:", error);
        }
      }

      setThumbnails(generatedThumbnails);
    };

    generateThumbnails();
  }, [documents]);

  // Keep the signature canvas width in sync with its actual on-screen
  // container. Re-runs whenever the user navigates to a different
  // document (the wrapper Box only mounts in the unsigned branch, so the
  // ref attaches there) and on any subsequent layout shift.
  useEffect(() => {
    const node = sigContainerRef.current;
    if (!node) {
      setSigWidth(0);
      return;
    }

    const measure = () => {
      const w = node.getBoundingClientRect().width;
      if (w > 0) {
        setSigWidth((prev) =>
          Math.abs(prev - w) > 1 ? Math.floor(w) : prev
        );
      }
    };

    measure();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(node);
    }
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [selectedDocument?.id, signedDocuments]);

  // Keep the rendered PDF page width in sync with its container so the
  // page never overflows the rounded border (which has overflow:hidden
  // and was visually cutting the document off).
  useEffect(() => {
    const node = pdfContainerRef.current;
    if (!node) {
      setPdfWidth(0);
      return;
    }

    const measure = () => {
      const w = node.getBoundingClientRect().width;
      if (w > 0) {
        setPdfWidth((prev) => (Math.abs(prev - w) > 1 ? Math.floor(w) : prev));
      }
    };

    measure();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(node);
    }
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [selectedDocument?.id]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const goToPreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const clearSignature = () => {
    signaturePadRef.current.clear();
  };

  const saveSignature = async () => {
    try {
      const base64Data = signaturePadRef.current.toDataURL("image/png");
      const blobData = await fetch(base64Data).then((res) => res.blob());

      const documentIndex = documents.findIndex(
        (doc) => doc.id === selectedDocument.id
      );

      if (documentIndex === -1) return;

      const bookingSecurityItems = booking?.securityItems?.items || [];
      const documentSecurityItem = bookingSecurityItems.find(
        (item) => item.type === "DOCUMENT"
      );

      if (!documentSecurityItem) {
        toast({
          title: "Error",
          description: "Document security item not found for this booking.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      setSignedDocuments((prev) => {
        const updated = [...prev];
        updated[documentIndex] = true;
        return updated;
      });

      onOpen();

      const signatureKey = `signatures/${
        documentSecurityItem.id
      }/document_${documentIndex + 1}_signature.png`;

      await Storage.put(signatureKey, blobData, {
        contentType: "image/png",
      });

      const currentSignatures = Array.isArray(documentSecurityItem.signatures)
        ? [...documentSecurityItem.signatures]
        : [];
      if (currentSignatures.length < documents.length) {
        const missingCount = documents.length - currentSignatures.length;
        currentSignatures.push(...Array(missingCount).fill(null));
      }
      currentSignatures[documentIndex] = signatureKey;

      const currentBooleanList = Array.isArray(
        documentSecurityItem.isCompleteSignatures
      )
        ? [...documentSecurityItem.isCompleteSignatures]
        : [];
      if (currentBooleanList.length < documents.length) {
        const missingCount = documents.length - currentBooleanList.length;
        currentBooleanList.push(...Array(missingCount).fill(false));
      }
      currentBooleanList[documentIndex] = true;

      const isComplete = currentBooleanList.every((value) => value);

      try {
        await apiAuth(
          `/security/booking-security-items/${documentSecurityItem.id}/sign`,
          {
            signatures: currentSignatures,
            isCompleteSignatures: currentBooleanList,
            isComplete,
          }
        );

        // Reflect the new state locally so other steps see it without a refetch
        documentSecurityItem.signatures = currentSignatures;
        documentSecurityItem.isCompleteSignatures = currentBooleanList;
        documentSecurityItem.isComplete = isComplete;
      } catch (apiError) {
        console.error("Error persisting signature state:", apiError);
        toast({
          title: "Error",
          description:
            "Signature uploaded, but we could not update the booking. Please try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (isComplete) {
        toast({
          title: "All documents signed",
          description: "You can continue to the next step.",
          status: "success",
          duration: 2500,
          isClosable: true,
        });
        // Back to the document list, where the Continue button now shows.
        setSelectedDocument(null);
      }
    } catch (error) {
      console.error("Error saving signature:", error);
      toast({
        title: "Error",
        description: "Failed to save signature.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={{ base: 4, md: 8 }} maxW="760px" mx="auto">
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent mx={8} top="30%">
          <ModalHeader fontSize={"14px"}>Signature Saved</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize={"14px"}>
              Your signature has been successfully saved!
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              bg="#817EFF"
              color="white"
              fontSize="13px"
              fontWeight={600}
              borderRadius="10px"
              h="40px"
              px={5}
              _hover={{ bg: "#6e6be6" }}
              _active={{ bg: "#5c59d4", transform: "scale(0.98)" }}
              transition="all 0.15s ease"
              onClick={() => {
                onClose();
                setSelectedDocument(null);
              }}
            >
              Back to Documents
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {selectedDocument ? (
        <>
          {/* Two-line header: stable title up top, doc name below with
              ellipsis. `minW={0}` is required on the flex parent and the
              text-wrapping child for `noOfLines` to actually clamp inside
              a flex row — without it, the text node can force the row
              wider than the viewport. */}
          <Flex alignItems="center" mb={4} gap={2} minW={0}>
            <IconButton
              icon={<Icon as={FaArrowLeft} />}
              variant="ghost"
              colorScheme="blue"
              bg="#2c3b4b"
              onClick={() => setSelectedDocument(null)}
              aria-label="Back"
              flexShrink={0}
            />

            <Box flex="1" minW={0}>
              <Text fontWeight="bold" fontSize="14px" lineHeight="1.2">
                Documents to sign
              </Text>
              <Text
                fontSize="11px"
                color="gray.400"
                noOfLines={1}
                title={selectedDocument.name}
                mt="2px"
              >
                {selectedDocument.name}
              </Text>
            </Box>
          </Flex>

          <Box
            ref={pdfContainerRef}
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="14px"
            overflow="hidden"
            mb={4}
            width="100%"
          >
            {pdfWidth > 0 ? (
              <Document
                file={selectedDocument.file}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(error) =>
                  console.error("Error loading PDF:", error)
                }
              >
                <Page
                  pageNumber={pageNumber}
                  // -2px so the page never rubs against the 1px borders
                  width={pdfWidth - 2}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            ) : (
              <Skeleton
                h="60vh"
                w="100%"
                startColor="gray.700"
                endColor="gray.600"
              />
            )}
          </Box>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <IconButton
              icon={<FaArrowLeft />}
              onClick={goToPreviousPage}
              isDisabled={pageNumber <= 1}
              aria-label="Previous Page"
              bg="whiteAlpha.100"
              color="white"
              borderRadius="10px"
              border="1px solid"
              borderColor="whiteAlpha.200"
              _hover={{ bg: "whiteAlpha.200" }}
              _active={{ transform: "scale(0.95)" }}
              _disabled={{ opacity: 0.3, cursor: "not-allowed" }}
              transition="all 0.15s ease"
            />
            <Text fontSize="12px" fontWeight={600} color="gray.400">
              Page {pageNumber} of {numPages}
            </Text>
            <IconButton
              icon={<FaArrowRight />}
              onClick={goToNextPage}
              isDisabled={pageNumber >= numPages}
              aria-label="Next Page"
              bg="whiteAlpha.100"
              color="white"
              borderRadius="10px"
              border="1px solid"
              borderColor="whiteAlpha.200"
              _hover={{ bg: "whiteAlpha.200" }}
              _active={{ transform: "scale(0.95)" }}
              _disabled={{ opacity: 0.3, cursor: "not-allowed" }}
              transition="all 0.15s ease"
            />
          </Flex>

          {!signedDocuments[
            documents.findIndex((doc) => doc.id === selectedDocument.id)
          ] && (
            <Box ref={sigContainerRef} borderRadius="lg" mb={4} mt={"20px"}>
              <Text fontSize={"12px"} fontWeight={"800"} mb={2} mt={2}>
                Signature Pad
              </Text>
              {sigWidth > 0 ? (
                // Keyed on `sigWidth` so the canvas remounts at the
                // correct dimensions when the container resizes
                // (orientation change, keyboard open/close on iOS, etc.).
                // react-signature-canvas does NOT pick up canvasProps
                // changes after mount.
                <SignaturePad
                  key={`sigpad-${selectedDocument.id}-${sigWidth}`}
                  ref={signaturePadRef}
                  canvasProps={{
                    width: sigWidth,
                    height: SIG_HEIGHT,
                    className: "signatureCanvas",
                    style: {
                      borderRadius: "8px",
                      // `display: block` kills the inline-block baseline
                      // gap that otherwise leaves a 4px sliver of
                      // background visible below the canvas.
                      display: "block",
                      width: "100%",
                      height: `${SIG_HEIGHT}px`,
                      touchAction: "none",
                    },
                  }}
                  backgroundColor="#f9f9f9"
                />
              ) : (
                // Placeholder while we measure on first paint. Same height
                // as the canvas so the layout doesn't jump once it
                // hydrates.
                <Skeleton
                  h={`${SIG_HEIGHT}px`}
                  w="100%"
                  borderRadius="8px"
                  startColor="gray.700"
                  endColor="gray.600"
                />
              )}
              <Flex gap={2} mt={4}>
                <Button
                  onClick={clearSignature}
                  fontSize="13px"
                  fontWeight={600}
                  h="44px"
                  borderRadius="10px"
                  variant="ghost"
                  color="gray.400"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  _hover={{ bg: "whiteAlpha.100" }}
                  _active={{ transform: "scale(0.98)" }}
                  transition="all 0.15s ease"
                  flexShrink={0}
                  px={5}
                >
                  Clear
                </Button>
                <Button
                  onClick={saveSignature}
                  fontSize="13px"
                  fontWeight={600}
                  h="44px"
                  borderRadius="10px"
                  flex={1}
                  bg="#817EFF"
                  color="white"
                  _hover={{ bg: "#6e6be6" }}
                  _active={{ bg: "#5c59d4", transform: "scale(0.98)" }}
                  transition="all 0.15s ease"
                >
                  Save Signature
                </Button>
              </Flex>
            </Box>
          )}
        </>
      ) : (
        <>
          <Flex alignItems="center" mb={4}>
            <IconButton
              icon={<Icon as={FaArrowLeft} />}
              variant="ghost"
              bg="#2c3b4b"
              mr={2}
              onClick={goToChecklist}
              aria-label="Back"
            />
            <Text fontWeight="bold" fontSize="14px">
              Documents to Review
            </Text>
          </Flex>

          {loading ? (
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  p={4}
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  borderRadius="14px"
                  bg="whiteAlpha.50"
                >
                  <Flex justifyContent="space-between" mb={3} alignItems="center">
                    <Skeleton h="14px" w="80px" borderRadius="6px" />
                    <Skeleton h="18px" w="18px" borderRadius="4px" />
                  </Flex>
                  <Skeleton h="100px" w="100%" borderRadius="8px" />
                </Box>
              ))}
            </Grid>
          ) : documents.length === 0 ? (
            (() => {
              const complete = stepComplete;
              const accentColor = complete ? "green.400" : "#817EFF";
              const accentRGB = complete ? "72,187,120" : "129,126,255";
              const IconComp = complete ? FaCheck : FaFileAlt;
              const heading = bypassed
                ? "Step waived"
                : complete
                ? "Documents already signed"
                : hasDocumentItem
                ? "No documents to sign"
                : "Step not required";
              const description = bypassed
                ? "Your host has waived document signing for this stay. There's nothing to do here."
                : complete
                ? "You've already signed all the required documents for this booking. You can safely continue."
                : hasDocumentItem
                ? "Your host hasn't uploaded any documents for this booking yet. You can safely continue."
                : "Your host hasn't enabled document signing for this stay. There's nothing to do here.";

              return (
                <VStack spacing={4} align="stretch" w="full">
                  <Box
                    position="relative"
                    bg={`linear-gradient(135deg, rgba(${accentRGB},0.10) 0%, rgba(${accentRGB},0.02) 100%)`}
                    border="1px solid"
                    borderColor={`rgba(${accentRGB},0.4)`}
                    borderRadius="14px"
                    p={4}
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      bottom={0}
                      w="3px"
                      bg={accentColor}
                    />
                    <Flex align="flex-start" gap={3}>
                      <Circle
                        size="32px"
                        bg={accentColor}
                        flexShrink={0}
                        mt={"2px"}
                      >
                        <Icon as={IconComp} boxSize="12px" color="white" />
                      </Circle>
                      <Box>
                        <Text
                          fontWeight={800}
                          fontSize={"15px"}
                          color="white"
                          lineHeight="1.3"
                        >
                          {heading}
                        </Text>
                        <Text
                          fontSize={"12px"}
                          color="gray.400"
                          lineHeight="1.5"
                          mt={1}
                        >
                          {description}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>

                  {(() => {
                    // Nothing to do on this screen, so the one button should
                    // point at the next thing the guest actually has to do.
                    const next = nextIncompleteStep(booking, "DOCUMENT");
                    return (
                      <Button
                        onClick={goToNextStep}
                        w="full"
                        h="52px"
                        bg="#817EFF"
                        color="white"
                        fontSize="15px"
                        fontWeight={700}
                        borderRadius="12px"
                        _hover={{
                          bg: "#6e6be6",
                          transform: "translateY(-1px)",
                          boxShadow: "0 6px 16px rgba(129,126,255,0.45)",
                        }}
                        _active={{
                          bg: "#5c59d4",
                          transform: "translateY(0)",
                        }}
                        transition="all 0.18s ease"
                        boxShadow="0 4px 12px rgba(129,126,255,0.35)"
                        leftIcon={next ? undefined : <Icon as={FaArrowLeft} />}
                        rightIcon={next ? <Icon as={FaArrowRight} /> : undefined}
                      >
                        {next ? `Continue to ${next.label}` : "Back to Checklist"}
                      </Button>
                    );
                  })()}
                </VStack>
              );
            })()
          ) : (
            <>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              {documents.map((doc, index) => (
                <GridItem
                  key={doc.id}
                  onClick={() => setSelectedDocument(doc)}
                  cursor="pointer"
                  textAlign="center"
                  p={4}
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  borderRadius="14px"
                  bg="whiteAlpha.50"
                  transition="all 0.15s ease"
                  _hover={{ bg: "whiteAlpha.100", borderColor: "rgba(129,126,255,0.4)" }}
                  _active={{ transform: "scale(0.98)" }}
                >
                  <Flex justifyContent={"space-between"} mb={3}>
                    <Text fontWeight="bold" fontSize="sm">
                      {`Document ${index + 1}`}
                    </Text>
                    <Checkbox
                      size="lg"
                      colorScheme={signedDocuments[index] ? "green" : "orange"}
                      isChecked={signedDocuments[index]}
                      isReadOnly
                    />
                  </Flex>
                  {thumbnails[doc.id] ? (
                    <Image
                      src={thumbnails[doc.id]}
                      alt={doc.name}
                      borderRadius="8px"
                      w="100%"
                      h="100px"
                      objectFit="cover"
                    />
                  ) : (
                    <Skeleton h="100px" w="100%" borderRadius="8px" />
                  )}
                </GridItem>
              ))}
            </Grid>
            {/* Every document signed → the guest decides when to move on. */}
            {documents.length > 0 &&
              documents.every((_, index) => signedDocuments[index]) &&
              (() => {
                const next = nextIncompleteStep(booking, "DOCUMENT");
                return (
                  <Button
                    onClick={goToNextStep}
                    mt={6}
                    w="full"
                    h="52px"
                    bg="#817EFF"
                    color="white"
                    fontSize="15px"
                    fontWeight={700}
                    borderRadius="12px"
                    _hover={{ bg: "#6e6be6" }}
                    _active={{ bg: "#5c59d4" }}
                    leftIcon={next ? undefined : <Icon as={FaArrowLeft} />}
                    rightIcon={next ? <Icon as={FaArrowRight} /> : undefined}
                  >
                    {next ? `Continue to ${next.label}` : "Back to Checklist"}
                  </Button>
                );
              })()}
            </>
          )}
        </>
      )}
    </Box>
  );
};
